import { 
  FileJson, 
  Clock, 
  HardDrive, 
  AlertTriangle,
  ArrowRightLeft,
  Globe,
  GitCompare
} from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  variant?: 'default' | 'warning' | 'error';
}

function StatCard({ icon, label, value, subValue, variant = 'default' }: StatCardProps) {
  return (
    <div className="card-hover p-4">
      <div className="flex items-start justify-between">
        <div
          className={cn(
            'p-2 rounded-lg',
            variant === 'default' && 'bg-brand-100 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400',
            variant === 'warning' && 'bg-yellow-100 dark:bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
            variant === 'error' && 'bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400'
          )}
        >
          {icon}
        </div>
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</p>
        <p className="text-sm text-slate-500 mt-1">{label}</p>
        {subValue && (
          <p className="text-xs text-slate-400 dark:text-slate-600 mt-1">{subValue}</p>
        )}
      </div>
    </div>
  );
}

export function StatsOverview() {
  const { stats, filteredEntries, compareHar, compareFileName } = useHARStore();

  if (!stats) return null;

  const domains = Object.keys(stats.byDomain).length;
  const filteredCount = filteredEntries.length;
  const isFiltered = filteredCount !== stats.totalRequests;

  return (
    <div className={cn(
      "grid gap-4",
      compareHar ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-7" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6"
    )}>
      <StatCard
        icon={<FileJson className="w-5 h-5" />}
        label="Total Requests"
        value={isFiltered ? `${filteredCount}/${stats.totalRequests}` : stats.totalRequests}
        subValue={isFiltered ? 'filtered' : undefined}
      />
      
      <StatCard
        icon={<Clock className="w-5 h-5" />}
        label="Total Time"
        value={formatTime(stats.totalTime)}
      />
      
      <StatCard
        icon={<HardDrive className="w-5 h-5" />}
        label="Total Size"
        value={formatBytes(stats.totalSize)}
      />
      
      <StatCard
        icon={<Globe className="w-5 h-5" />}
        label="Domains"
        value={domains}
      />
      
      <StatCard
        icon={<ArrowRightLeft className="w-5 h-5" />}
        label="Redirects"
        value={stats.redirects}
        variant={stats.redirects > 5 ? 'warning' : 'default'}
      />
      
      <StatCard
        icon={<AlertTriangle className="w-5 h-5" />}
        label="Errors"
        value={stats.errors}
        variant={stats.errors > 0 ? 'error' : 'default'}
      />

      {compareHar && (
        <StatCard
          icon={<GitCompare className="w-5 h-5" />}
          label="Comparing With"
          value={compareFileName?.length && compareFileName.length > 15 ? compareFileName.substring(0, 12) + '...' : compareFileName || 'Unknown'}
          variant="default"
        />
      )}
    </div>
  );
}
